/**
 * apps/dashboard/src/api/hooks/useWorkspaceHome.ts
 *
 * Named React hook for the workspaceHome composite.
 * Returns a discriminated union (discriminant: `status`) with five variants:
 * loading | ready | empty | error | forbidden.
 *
 * All React hook calls are unconditional and precede any early return
 * (React Rules of Hooks).
 */
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useSubscriptionActive } from "../../components/layout/SubscriptionScope";
import { transformWorkspaceHome } from "../transforms/workspaceHome";
import type { WorkspaceHomeView } from "../../../../../shared/types/workspaceHome";

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

export type WorkspaceHomeStatus = "loading" | "ready" | "empty" | "error" | "forbidden";

export type WorkspaceHomeHookResult =
  | { status: "loading" }
  | { status: "ready"; data: WorkspaceHomeView }
  | { status: "empty" }
  | { status: "error"; error: string }
  | { status: "forbidden" };

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWorkspaceHome(
  mode: string,
  hookArgs?: Record<string, string>,
): WorkspaceHomeHookResult {
  // (a) Subscription gate — unconditional
  const active = useSubscriptionActive();

  // (b) Compose query args
  const queryArgs = active ? { mode, ...hookArgs } : "skip";

  // (c) Subscribe — unconditional hook call
  const raw = useQuery(
    api.composites.workspaceHome.workspaceHome,
    queryArgs as typeof queryArgs,
  );

  // (d) Discriminate
  if (!active || raw === undefined) {
    return { status: "loading" };
  }

  if (raw === null) {
    return { status: "empty" };
  }

  try {
    const data = transformWorkspaceHome(raw as { mode: string; [key: string]: unknown });
    return { status: "ready", data };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);

    if (message.includes("FORBIDDEN") || message.includes("not-a-member")) {
      return { status: "forbidden" };
    }

    return { status: "error", error: message };
  }
}
